import getMenuTree from "../ApiV2/getMenuTree";

/**
 * Optimized theme/location loader with dual-tier caching
 * Checks: sessionStorage -> localStorage -> API
 * Uses getMenuTree API to fetch both theme data AND menu data to avoid duplicate calls
 * Returns a Promise that resolves with { theme, locationName, isContactable }
 * Also caches the menu data for MenuCatigoriesMain to use
 */
export const loadThemeData = (location) => {
    return new Promise((resolve, reject) => {
        if (!location) {
            console.log("location not provided");
            reject(new Error("No location provided"));
            return;
        }

        const sessionKeyTheme = location + "-theme";
        const sessionKeyName = location + "-name";
        const sessionKeyContactable = location + "-isContactable";
        const localKeyTheme = "theme-" + location;
        const localKeyName = "name-" + location;
        const localKeyContactable = "isContactable-" + location;

        // TIER 1: Check sessionStorage (fastest, current tab only)
        let locationTheme = window.sessionStorage.getItem(sessionKeyTheme);
        let locationName = window.sessionStorage.getItem(sessionKeyName);
        let isContactable = window.sessionStorage.getItem(sessionKeyContactable);

        if (locationTheme && locationName && isContactable !== null) {
            console.log("✓ Retrieved from sessionStorage: " + locationTheme);
            resolve({ theme: locationTheme, name: locationName, isContactable: isContactable === 'true' });
            return;
        }

        // TIER 2: Check localStorage (persistent across tabs/sessions)
        locationTheme = window.localStorage.getItem(localKeyTheme);
        locationName = window.localStorage.getItem(localKeyName);
        isContactable = window.localStorage.getItem(localKeyContactable);

        if (locationTheme && locationName && isContactable !== null) {
            console.log("✓ Retrieved from localStorage: " + locationTheme);
            // Sync to sessionStorage for faster access
            window.sessionStorage.setItem(sessionKeyTheme, locationTheme);
            window.sessionStorage.setItem(sessionKeyName, locationName);
            window.sessionStorage.setItem(sessionKeyContactable, isContactable);
            resolve({ theme: locationTheme, name: locationName, isContactable: isContactable === 'true' });
            return;
        }

        // TIER 3: Call getMenuTree API if not cached
        // This provides both theme data AND menu data, eliminating duplicate API calls
        console.log("⏳ Fetching from getMenuTree API...");
        getMenuTree(location, true)
            .then(response => {
                // Check HTTP response status first
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                // Check if API returned valid data
                if (!data || !data.locationName) {
                    console.log("Invalid location data returned:", data);
                    throw new Error("Location not found");
                }

                const themeName = data.theme_name;
                const name = data.locationName;
                const contactable = data.isContactable !== false;

                if (!themeName || !name) {
                    console.warn("Missing theme or location name:", { themeName, name });
                    throw new Error("Invalid location data");
                }

                console.log("✓ Retrieved from API: " + themeName);

                // Cache theme data in both sessionStorage and localStorage
                window.sessionStorage.setItem(sessionKeyTheme, themeName);
                window.sessionStorage.setItem(sessionKeyName, name);
                window.sessionStorage.setItem(sessionKeyContactable, contactable.toString());
                window.localStorage.setItem(localKeyTheme, themeName);
                window.localStorage.setItem(localKeyName, name);
                window.localStorage.setItem(localKeyContactable, contactable.toString());

                // Also cache the menu data so MenuCatigoriesMain doesn't duplicate the API call
                if (data.menus && data.menus.length > 0) {
                    const menuCacheKey = `menuTree-${location}`;
                    cacheAPIResponse(menuCacheKey, data.menus);
                    console.log("✓ Cached menu data for later use");
                }

                resolve({ theme: themeName, name: name, isContactable: contactable });
            })
            .catch((error) => {
                console.error("Error loading theme data:", error);
                reject(error);
            });
    });
};

/**
 * @deprecated Use `useThemeLoader` hook instead. This function is replaced by the new hook which provides
 * better React integration, proper loading states, and error handling.
 * 
 * Legacy function - kept for backward compatibility only.
 * The new architecture loads theme once in MainHeader using useThemeLoader hook,
 * which is then inherited by all child components.
 */
export const setThemeWithSessionStorage = (location, setLocationName) => {
    loadThemeData(location)
        .then(({ theme, name }) => {
            document.getElementById("app-root").classList.add(theme);
            if (setLocationName) {
                setLocationName(name);
            }
        })
        .catch((error) => {
            console.error(error);
        });
};

/**
 * Retrieve the cached location name from sessionStorage or localStorage
 * Uses the same key pattern as loadThemeData
 * Returns the location name or empty string if not found
 */
export const getLocationName = (location) => {
    if (!location) return "";
    
    // Try sessionStorage first (faster)
    let name = window.sessionStorage.getItem(location + "-name");
    if (name) return name;
    
    // Fall back to localStorage
    name = window.localStorage.getItem("name-" + location);
    return name || "";
};

/**
 * Retrieve the cached IsContactable flag from sessionStorage or localStorage
 * Uses the same key pattern as loadThemeData
 * Returns true by default if not found
 */
export const getIsContactable = (location) => {
    if (!location) return true;
    
    // Try sessionStorage first (faster)
    let contactable = window.sessionStorage.getItem(location + "-isContactable");
    if (contactable !== null) return contactable === 'true';
    
    // Fall back to localStorage
    contactable = window.localStorage.getItem("isContactable-" + location);
    return contactable === 'true' || true; // Default to true if not found
};

/**
 * Cache API response data with expiration at end of current day
 * Useful for menu items, submenu data that updates daily
 * 
 * @param {string} key - Unique cache key (e.g., "menuItems-123")
 * @param {any} data - Data to cache
 */
export const cacheAPIResponse = (key, data) => {
    const cacheKey = `api-cache-${key}`;
    const expirationTime = getEndOfDayTimestamp();
    
    const cacheEntry = {
        data: data,
        expiresAt: expirationTime
    };
    
    try {
        window.sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
        console.log(`✓ Cached: ${key} (expires at ${new Date(expirationTime).toLocaleTimeString()})`);
    } catch (error) {
        console.error("Error caching API response:", error);
    }
};

/**
 * Retrieve cached API response if not expired
 * Returns null if cache miss or expired
 * 
 * @param {string} key - Unique cache key (e.g., "menuItems-123")
 * @returns {any|null} - Cached data or null if expired/not found
 */
export const getCachedResponse = (key) => {
    const cacheKey = `api-cache-${key}`;
    
    try {
        const cached = window.sessionStorage.getItem(cacheKey);
        if (!cached) return null;
        
        const cacheEntry = JSON.parse(cached);
        const now = new Date().getTime();
        
        // Check if cache has expired
        if (now > cacheEntry.expiresAt) {
            console.log(`⏰ Cache expired: ${key}`);
            window.sessionStorage.removeItem(cacheKey);
            return null;
        }
        
        console.log(`✓ Cache hit: ${key}`);
        return cacheEntry.data;
    } catch (error) {
        console.error("Error retrieving cached response:", error);
        return null;
    }
};

/**
 * Calculate Unix timestamp for end of current day (23:59:59)
 * Used for daily cache expiration
 * 
 * @returns {number} - Unix timestamp of end of day in milliseconds
 */
export const getEndOfDayTimestamp = () => {
    const now = new Date();
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return endOfDay.getTime();
};
